import React, { useEffect, useState, useCallback } from "react";
import { Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS, BRAND } from "../data/copperx";

export default function Navbar({ onQuoteClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const onScroll = useCallback(() => setScrolled(window.scrollY > 24), []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur border-b border-[#E2E8F0]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-home-link" className="shrink-0">
          <Logo variant={scrolled ? "dark" : "dark"} />
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-[#0F172A] hover:text-[#B85C38] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:${BRAND.phoneHref}`}
            data-testid="nav-phone-link"
            className="flex items-center gap-2 text-sm font-medium text-[#475569] hover:text-[#B85C38] transition-colors"
          >
            <Phone size={14} /> {BRAND.phone}
          </a>
          <button
            onClick={onQuoteClick}
            data-testid="nav-request-quote-button"
            className="bg-[#B85C38] hover:bg-[#D97757] text-white px-5 py-2.5 text-sm font-medium tracking-wide transition-colors"
          >
            Request a Quote
          </button>
        </div>

        <button
          className="lg:hidden text-[#0F172A]"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-[#E2E8F0] px-6 py-6"
        >
          <div className="flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-base font-medium text-[#0F172A] py-2 border-b border-[#F1F5F9]"
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                onQuoteClick?.();
              }}
              data-testid="mobile-request-quote-button"
              className="bg-[#B85C38] text-white px-5 py-3 text-sm font-medium mt-2"
            >
              Request a Quote
            </button>
            <a
              href={`tel:${BRAND.phoneHref}`}
              className="text-sm text-[#475569] mt-1"
              data-testid="mobile-phone-link"
            >
              {BRAND.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
